import {
  ClockCircleOutlined,
  ControlOutlined,
  MailOutlined,
} from '@ant-design/icons'
import FormCard from '@components/FormCard'
import Page from '@components/Page/Page'
import { TextEditorVariable } from '@components/tiptap/tiptap-ui/variables-dropdown/variables-dropdown'
import { CommunicationKey } from '@models/Communication'
import { NotificationContentKey } from '@models/NotificationContent'
import { SettingKey } from '@models/Setting'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getNotificationContents } from '@store/NotificationContentReducer'
import { getSettings, selectSettings } from '@store/SettingReducer'
import { SBRMAction } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import MetadataKey from '@utils/MetadataKey'
import { Permission } from '@utils/permissions'
import { Col, Flex, Row } from 'antd'
import { useEffect } from 'react'

const UserDataPrivacyPage = () => {
  const dispatch = useAppDispatch()
  const settings = useAppSelector(selectSettings())

  const fetchSettings = () => {
    dispatch(
      getNotificationContents([
        NotificationContentKey.GDPR_ACCOUNT_RETENTION_WARNING,
      ])
    )
    dispatch(
      getSettings([
        SettingKey.GDPR_PURGE_ENABLED,
        SettingKey.GDPR_PURGE_RETENTION_DURATION,
        SettingKey.GDPR_PURGE_NOTICE_ENABLED,
        SettingKey.GDPR_PURGE_NOTICE_DELAY,
      ])
    )
  }
  useEffect(() => {
    fetchSettings()
  }, [])

  const findSetting = (key: SettingKey) => settings.find((s) => s.key === key)

  const gdprPurgeRetentionDurationValue = findSetting(
    SettingKey.GDPR_PURGE_RETENTION_DURATION
  )?.value
  const gdprPurgeNoticeDelayValue = findSetting(
    SettingKey.GDPR_PURGE_NOTICE_DELAY
  )?.value

  const formatDuration = (value: number | string) =>
    `${value} day${Number(value) > 1 ? 's' : ''}`

  return (
    <Page permissions={Permission.createAdmin}>
      <Flex vertical gap={100}>
        <Flex vertical gap={0}>
          <Row gutter={[32, 32]}>
            <Col span={12}>
              <FormCard
                title="Purge activation"
                description={`Automatic data anomyzation purge is enabled.`}
                icon={<ControlOutlined />}
                entity={Entity.setting}
                action={SBRMAction.Update}
                metadata={[
                  {
                    key: MetadataKey.Setting,
                    value: SettingKey.GDPR_PURGE_ENABLED,
                  },
                ]}
              />
            </Col>
            <Col span={12}>
              <FormCard
                title="Retention duration"
                description={`With the current settings, user data is retained for ${formatDuration(gdprPurgeRetentionDurationValue)} after the last interaction before being permanently anonymized by the purge.`}
                icon={<ClockCircleOutlined />}
                entity={Entity.setting}
                action={SBRMAction.Update}
                metadata={[
                  {
                    key: MetadataKey.Setting,
                    value: SettingKey.GDPR_PURGE_RETENTION_DURATION,
                  },
                  {
                    key: MetadataKey.ExtraProps,
                    value: {
                      min: 1,
                      max: 1095,
                      suffix: 'Days',
                    },
                  },
                ]}
              />
            </Col>
            <Col span={12}>
              <FormCard
                title="Email notification before purge"
                description={`Automatic emails for data purge are enabled.`}
                icon={<ControlOutlined />}
                entity={Entity.setting}
                action={SBRMAction.Update}
                metadata={[
                  {
                    key: MetadataKey.Setting,
                    value: SettingKey.GDPR_PURGE_NOTICE_ENABLED,
                  },
                ]}
              />
            </Col>
            <Col span={12}>
              <FormCard
                title="Email notification delay"
                description={`With the current settings, users will receive a purge notification ${formatDuration(gdprPurgeNoticeDelayValue)} before the anonymization date.`}
                icon={<ClockCircleOutlined />}
                entity={Entity.setting}
                action={SBRMAction.Update}
                metadata={[
                  {
                    key: MetadataKey.Setting,
                    value: SettingKey.GDPR_PURGE_NOTICE_DELAY,
                  },
                  {
                    key: MetadataKey.ExtraProps,
                    value: {
                      min: 1,
                      max: 90,
                      suffix: 'Days',
                    },
                  },
                ]}
              />
            </Col>
            <Col span={24}>
              <FormCard
                title="Email notification content"
                icon={<MailOutlined />}
                entity={Entity.notificationContent}
                action={SBRMAction.Update}
                metadata={[
                  {
                    key: MetadataKey.NotificationContent,
                    value:
                      NotificationContentKey.GDPR_ACCOUNT_RETENTION_WARNING,
                  },
                  {
                    key: MetadataKey.textEditorVariables,
                    value: [
                      TextEditorVariable.FIRST_NAME,
                      TextEditorVariable.LAST_NAME,
                      TextEditorVariable.EMAIL,
                      TextEditorVariable.RETENTION_URL,
                    ],
                  },
                  {
                    key: MetadataKey.Communication,
                    value: CommunicationKey.EMAIL_ACCOUNT_RETENTION_WARNING,
                  },
                ]}
              />
            </Col>
          </Row>
        </Flex>
      </Flex>
    </Page>
  )
}

export default UserDataPrivacyPage
